// What the meta layer reads: records (a kind and a flat payload) the game makes from its own events, matched by rules
// as data. A rule names a record kind (`on`) and, optionally, field values the payload must have (`match`); a value
// list means any of them. Templates (`"${kind}"`) fill an id from a record's fields. Facts come in through a test
// function the game passes (a condition string in, a boolean out: spike 002 passes @rpg/story's `Conditions`).

/** A value in a record's payload. */
export type MetaValue = number | string | boolean | null;

/** A record the game feeds the meta layer: a kind ("defeated", "found") and its payload. */
export interface MetaRecord {
  kind: string;
  data: Readonly<Record<string, MetaValue>>;
}

/** Field values a record must have: one value, or any of a list. */
export type Match = Readonly<Record<string, MetaValue | readonly MetaValue[]>>;

/** Evaluate a condition string (the game's fact language). Throws or returns false for what it can't answer. */
export type FactTest = (condition: string) => boolean;

/** A fact source in the shape @rpg/story's `Conditions.register` takes (`name.key` facts). */
export interface MetaFactSource {
  name: string;
  get(key: string): MetaValue;
  knows(key: string): boolean;
  keys(): readonly string[];
}

/** Whether a record's payload has every field `match` asks for. */
export function matches(data: Readonly<Record<string, MetaValue>>, match: Match | undefined): boolean {
  if (!match) return true;
  for (const [k, want] of Object.entries(match)) {
    const v = data[k] ?? null;
    if (Array.isArray(want) ? !(want as readonly MetaValue[]).includes(v) : v !== want) return false;
  }
  return true;
}

/** Fill `${field}` in a template from a record's payload; null when a field is missing. */
export function fill(template: string, data: Readonly<Record<string, MetaValue>>): string | null {
  let missing = false;
  const out = template.replace(/\$\{(\w+)\}/g, (_, k: string) => {
    const v = data[k];
    if (v === undefined || v === null) {
      missing = true;
      return "";
    }
    return String(v);
  });
  return missing ? null : out;
}
